import { useCallback } from "react";

import { Box, Flex, Image } from "@chakra-ui/react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router";

import { TGTGBagPreparationFeatures, TGTGBagUpdateStates } from "analytics/events";
import { routes } from "config/routes";
import FlinkBagBeverages from "images/rescue-bag/alcohol_rescue_bag.png";
import FlinkBagBread from "images/rescue-bag/bakery_rescue_bag.png";
import FlinkBagCatFood from "images/rescue-bag/cat_food_rescue_bag.png";
import FlinkBagDogFood from "images/rescue-bag/dog_food_rescue_bag.png";
import FlinkBagEaster from "images/rescue-bag/easter_rescue_bag.png";
import FlinkBagFestive from "images/rescue-bag/festive.png";
import FlinkBagFrozenItems from "images/rescue-bag/frozen_rescue_bag.png";
import FlinkFruitsAndVegetables from "images/rescue-bag/fruits_and_vegetables_rescue_bag.png";
import FlinkBagMeat from "images/rescue-bag/meat_rescue_bag.png";
import FlinkBagNonFood from "images/rescue-bag/non_food_rescue_bag.png";
import FlinkBag from "images/rescue-bag/rescue_bag.png";
import FlinkBagXL from "images/rescue-bag/xl_rescue_bag.png";
import { Page } from "shared/components/Page";
import { useAnalytics } from "shared/hooks/useAnalytics";
import { IntlMessageId } from "shared/lang";
import { NavigationHeader } from "ui/NavigationHeader/NavigationHeader";
import { BodyS, DetailS, TitleXXS } from "ui/Typography/Typography";

import { BagType, useBagTypesFeatureFlag } from "../hooks/useBagTypeFeatureFlag";
import { useTGTGBagStore } from "../stores/useTGTGBagStore";

const BAG_IMAGES: Record<string, string> = {
  alcoholic_beverages: FlinkBagBeverages,
  bakery: FlinkBagBread,
  frozen_items: FlinkBagFrozenItems,
  non_food: FlinkBagNonFood,
  fruits_and_vegetables: FlinkFruitsAndVegetables,
  festive: FlinkBagFestive,
  easter: FlinkBagEaster,
  xl: FlinkBagXL,
  meat: FlinkBagMeat,
  cat_food: FlinkBagCatFood,
  dog_food: FlinkBagDogFood,
};

function Menucard({
  imageUrl,
  title,
  price,
  onClick,
}: {
  imageUrl: string;
  title: string;
  price: number;
  onClick: () => void;
}) {
  const intl = useIntl();
  return (
    <Box
      minW="128px"
      maxW="128px"
      borderRadius="md"
      position="relative"
      overflow="hidden"
      boxShadow="md"
      onClick={onClick}
    >
      <Image src={imageUrl} alt={title} objectFit="cover" w="full" h="full" borderRadius="md" />
      <Box
        position="absolute"
        bottom={0}
        left={0}
        right={0}
        bg="rgba(248, 248, 248, 0.8)"
        px="s100"
        py="s50"
      >
        <TitleXXS>
          {intl.formatMessage({
            id: `pages.tgtg-bag-selection-page.bag-types.${title}` as IntlMessageId,
          })}
        </TitleXXS>
        <DetailS>
          {intl.formatMessage({ id: "pages.tgtg-bag-selection-page.bag-types.price" }, { price })}
        </DetailS>
      </Box>
    </Box>
  );
}

export function TGTGBagSelectionPage() {
  const navigate = useNavigate();
  const intl = useIntl();
  const { sendSegmentTrackEvent } = useAnalytics();

  const { bagTypes: bagTypesData } = useBagTypesFeatureFlag();
  const { setSelectedBag } = useTGTGBagStore();
  const handleBagSelect = useCallback(
    ({
      key,
      minimumValue,
      maximumThreshold,
      sku,
      maximumUnitsPerSku,
      minimumDistinctSkus,
      allowedCategories,
      excludedCategories,
    }: BagType) => {
      const bagId = crypto.randomUUID();
      if (bagId && sku) {
        setSelectedBag(
          bagId,
          key,
          minimumValue,
          sku,
          maximumThreshold,
          maximumUnitsPerSku,
          minimumDistinctSkus,
          allowedCategories,
          excludedCategories,
        );
        sendSegmentTrackEvent("bagStateUpdated", {
          state: TGTGBagUpdateStates.BagPreparationStarted,
          bag_id: bagId,
          bag_value_final: null,
          bag_value_target: minimumValue,
          bag_type: key,
          bag_value_limit: minimumValue + Number(maximumThreshold),
          bag_preparation_feature: TGTGBagPreparationFeatures.TgtgAssistant,
        });
        navigate(routes.inventory.tooGoodToGo.prepareBag);
      }
    },
    [navigate, sendSegmentTrackEvent, setSelectedBag],
  );

  return (
    <Page isFull isBgGrey h="100%" justifyContent="flex-start">
      <NavigationHeader
        title={intl.formatMessage({ id: "pages.tgtg-bag-selection-page.title" })}
        onClickGoBack={() => navigate(routes.inventory.root)}
      />
      <Flex
        direction="column"
        align="center"
        p="s300"
        gap="s200"
        overflowY="scroll"
        css={{
          "&::-webkit-scrollbar": {
            display: "none",
          },
        }}
      >
        <BodyS>{intl.formatMessage({ id: "pages.tgtg-bag-selection-page.sub-title" })}</BodyS>
        {bagTypesData && (
          <Flex maxW="280px" wrap="wrap" columnGap="s300" rowGap="s200" justify="flex-start">
            {bagTypesData.map((bag) => (
              <Menucard
                key={bag.key}
                imageUrl={BAG_IMAGES[bag.key] || FlinkBag}
                title={bag.key}
                price={bag.minimumValue}
                onClick={() => handleBagSelect(bag)}
              />
            ))}
          </Flex>
        )}
      </Flex>
    </Page>
  );
}
